import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText
} from "@solomon-apps/shared/src/components/ui/input-group";
import {
    RadioGroup,
    RadioGroupItem
} from "@solomon-apps/shared/src/components/ui/radio-group";
import { SegmentedSelect } from "@solomon-apps/shared/src/components/ui/segmented-select";
import { Slider } from "@solomon-apps/shared/src/components/ui/slider";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import React from "react";

export interface OptionPolicyConstraint {
    type: "options";
    options: { value: string; label: string }[];
}

// ---------------------------------------------------------------------------
// Shared slider primitive — editorial-flat aesthetic (hairline track,
// square thumb). Used by all slider-based inputs below.
// ---------------------------------------------------------------------------

interface EditorialSliderProps {
    value: number;
    onValueChange: (next: number) => void;
    min: number;
    max: number;
    step?: number;
    ticks?: number[];
    capAt?: number;
}

export function EditorialSlider({
    value,
    onValueChange,
    min,
    max,
    step = 1,
    ticks,
    capAt
}: EditorialSliderProps): React.ReactElement {
    const pct = (n: number): number => ((n - min) / (max - min)) * 100;

    return (
        <div className="relative w-full">
            <Slider
                value={[value]}
                onValueChange={(values) => {
                    const next = Array.isArray(values) ? values[0] : values;
                    if (typeof next === "number") onValueChange(next);
                }}
                min={min}
                max={max}
                step={step}
                className={cn(
                    "[&_[data-slot=slider-control]]:py-3 [&_[data-slot=slider-control]]:cursor-pointer",
                    "[&_[data-slot=slider-track]]:h-px [&_[data-slot=slider-track]]:rounded-none [&_[data-slot=slider-track]]:bg-line-base",
                    "[&_[data-slot=slider-range]]:bg-foreground-base",
                    "[&_[data-slot=slider-thumb]]:size-2.5 [&_[data-slot=slider-thumb]]:rounded-none [&_[data-slot=slider-thumb]]:border-0 [&_[data-slot=slider-thumb]]:bg-foreground-base [&_[data-slot=slider-thumb]]:shadow-none"
                )}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-3"
            >
                {ticks?.map((t) => (
                    <span
                        key={t}
                        className="absolute top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2 bg-line-dark"
                        style={{ left: `${pct(t)}%` }}
                    />
                ))}
                {typeof capAt === "number" ? (
                    <span
                        className="absolute top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-foreground-base"
                        style={{ left: `${pct(capAt)}%` }}
                    />
                ) : null}
            </div>
        </div>
    );
}

export function TickLabels({
    ticks,
    min,
    max,
    format = (n): string => String(n),
    onTickClick
}: {
    ticks: number[];
    min: number;
    max: number;
    format?: (n: number) => string;
    onTickClick?: (value: number) => void;
}): React.ReactElement {
    return (
        <div className="relative mt-1 h-4 w-full">
            {ticks.map((t) => (
                <Text
                    key={t}
                    variant="fine-print"
                    render={
                        onTickClick ? (
                            <button
                                type="button"
                                onClick={() => onTickClick(t)}
                            />
                        ) : (
                            <span />
                        )
                    }
                    className={cn(
                        "absolute -translate-x-1/2 text-foreground-neutral",
                        onTickClick &&
                            "cursor-pointer hover:text-foreground-base transition-colors"
                    )}
                    style={{ left: `${((t - min) / (max - min)) * 100}%` }}
                >
                    {format(t)}
                </Text>
            ))}
        </div>
    );
}

export function formatMagnitude(n: number): string {
    if (n >= 1_000_000)
        return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
    return String(n);
}

// ---------------------------------------------------------------------------
// Controlled input widgets — accept draft + onDraftChange from parent.
// No internal state: the parent policy editor owns the value.
// ---------------------------------------------------------------------------

const TIER_OPTIONS = [
    { value: "1", label: "Tier 1", caption: "Basic ID" },
    { value: "2", label: "Tier 2", caption: "Photo + Address" },
    { value: "3", label: "Tier 3", caption: "Notarized" }
];

export function TierSegmentedInput({
    draft,
    onDraftChange,
    min = 1,
    max = TIER_OPTIONS.length
}: {
    draft: string;
    onDraftChange: (value: string) => void;
    /** Allowed tier range — tiers outside [min, max] are disabled. */
    min?: number;
    max?: number;
}): React.ReactElement {
    const tier = draft || "1";

    const options = TIER_OPTIONS.map((option) => {
        const n = Number(option.value);
        return { ...option, disabled: n < min || n > max };
    });

    return (
        <SegmentedSelect
            options={options}
            value={tier}
            onValueChange={onDraftChange}
        />
    );
}

export function DaysSliderInput({
    draft,
    onDraftChange,
    cap
}: {
    draft: string;
    onDraftChange: (value: string) => void;
    cap: number;
}): React.ReactElement {
    const days = Math.max(0, Math.min(cap, Number(draft) || 0));
    const ticks = Array.from(
        new Set([0, Math.round(cap / 3), Math.round((2 * cap) / 3), cap])
    );

    return (
        <div className="grid grid-cols-[minmax(0,1fr)_10rem] gap-6 items-center">
            <div>
                <EditorialSlider
                    value={days}
                    onValueChange={(next) => onDraftChange(String(next))}
                    min={0}
                    max={cap}
                    ticks={ticks}
                />
                <TickLabels
                    ticks={ticks}
                    min={0}
                    max={cap}
                    format={(n) => `${n}d`}
                    onTickClick={(n) => onDraftChange(String(n))}
                />
            </div>
            <InputGroup className="h-12">
                <InputGroupInput
                    value={draft}
                    inputMode="numeric"
                    onChange={(event) => {
                        const filtered = event.target.value.replace(/\D/g, "");
                        const n = Number(filtered);
                        onDraftChange(String(Math.max(0, Math.min(cap, n))));
                    }}
                    numeric
                    className="h-full text-2xl text-center"
                />
                <InputGroupAddon align="inline-end">
                    <InputGroupText>days</InputGroupText>
                </InputGroupAddon>
            </InputGroup>
        </div>
    );
}

export function PercentageSplitInput({
    draft,
    onDraftChange
}: {
    draft: string;
    onDraftChange: (value: string) => void;
}): React.ReactElement {
    const pct = Math.max(0, Math.min(100, Number(draft) || 0));

    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between">
                <Text variant="fine-print" className="text-foreground-muted">
                    You
                </Text>
                <Text variant="fine-print" className="text-foreground-muted">
                    Distributor
                </Text>
            </div>
            <div className="relative h-8 w-full overflow-hidden border border-line-base bg-surface-raised">
                <div
                    className="absolute inset-y-0 left-0 bg-foreground-base/85"
                    style={{ width: `${pct}%` }}
                />
                <span
                    className="absolute inset-y-0 w-px bg-foreground-base"
                    style={{ left: `${pct}%` }}
                />
                <Text
                    variant="number"
                    render={<span />}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-inverse"
                >
                    {pct}%
                </Text>
                <Text
                    variant="number"
                    render={<span />}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-base"
                >
                    {100 - pct}%
                </Text>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center gap-6">
                <EditorialSlider
                    value={pct}
                    onValueChange={(next) => onDraftChange(String(next))}
                    min={0}
                    max={100}
                    ticks={[0, 25, 50, 75, 100]}
                />
                <InputGroup className="h-10">
                    <InputGroupInput
                        value={draft}
                        inputMode="numeric"
                        onChange={(event) => {
                            const filtered = event.target.value.replace(
                                /\D/g,
                                ""
                            );
                            const n = Number(filtered);
                            onDraftChange(
                                String(Math.max(0, Math.min(100, n)))
                            );
                        }}
                        numeric
                        className="h-full text-center"
                    />
                    <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                    </InputGroupAddon>
                </InputGroup>
            </div>
        </div>
    );
}

export function CurrencyCappedInput({
    draft,
    onDraftChange,
    cap,
    unit
}: {
    draft: string;
    onDraftChange: (value: string) => void;
    cap: number;
    unit: string;
}): React.ReactElement {
    const amount = Math.max(0, Math.min(cap, Number(draft) || 0));
    const chips = [
        {
            label: formatMagnitude(Math.round(cap * 0.25)),
            value: Math.round(cap * 0.25)
        },
        {
            label: formatMagnitude(Math.round(cap * 0.5)),
            value: Math.round(cap * 0.5)
        },
        { label: `${formatMagnitude(cap)} (cap)`, value: cap }
    ];
    const ticks = Array.from(new Set(chips.map((c) => c.value)));

    return (
        <div className="space-y-7">
            <div className="px-1">
                <EditorialSlider
                    value={amount}
                    onValueChange={(next) => onDraftChange(String(next))}
                    min={0}
                    max={cap}
                    step={Math.max(1, Math.round(cap / 100))}
                    ticks={ticks}
                    capAt={cap}
                />
                <TickLabels
                    ticks={ticks}
                    min={0}
                    max={cap}
                    format={formatMagnitude}
                    onTickClick={(n) => onDraftChange(String(n))}
                />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
                <InputGroup className="h-12 w-full max-w-[18rem]">
                    {unit ? (
                        <InputGroupAddon>
                            <InputGroupText>{unit}</InputGroupText>
                        </InputGroupAddon>
                    ) : null}
                    <InputGroupInput
                        value={draft}
                        inputMode="numeric"
                        onChange={(event) => {
                            const filtered = event.target.value.replace(
                                /\D/g,
                                ""
                            );
                            const n = Number(filtered);
                            onDraftChange(
                                String(Math.max(0, Math.min(cap, n)))
                            );
                        }}
                        numeric
                        className="h-full text-xl text-right"
                    />
                </InputGroup>
                <div className="flex flex-wrap gap-2">
                    {chips.map((chip) => (
                        <button
                            key={chip.value}
                            type="button"
                            onClick={() => onDraftChange(String(chip.value))}
                            className={cn(
                                "border border-line-base bg-surface-raised px-3 py-2 transition-colors hover:border-line-dark",
                                amount === chip.value &&
                                    "border-foreground-base bg-surface-inverse-raised text-foreground-inverse"
                            )}
                        >
                            <Text variant="fine-print" render={<span />}>
                                {chip.label}
                            </Text>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function CurrencyDelegatedInput({
    draft,
    onDraftChange,
    unit
}: {
    draft: string;
    onDraftChange: (value: string) => void;
    unit: string;
}): React.ReactElement {
    const amount = Math.max(0, Number(draft) || 0);
    const chips = [1_000, 10_000, 100_000, 1_000_000];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                <InputGroup className="h-14">
                    {unit ? (
                        <InputGroupAddon>
                            <InputGroupText>{unit}</InputGroupText>
                        </InputGroupAddon>
                    ) : null}
                    <InputGroupInput
                        value={draft}
                        inputMode="numeric"
                        onChange={(event) => {
                            const filtered = event.target.value.replace(
                                /\D/g,
                                ""
                            );
                            onDraftChange(filtered);
                        }}
                        numeric
                        className="h-full text-2xl"
                    />
                </InputGroup>
                <Text
                    variant="number"
                    className="text-foreground-neutral text-xl"
                >
                    = {formatMagnitude(amount)}
                </Text>
            </div>
            <div className="flex flex-wrap gap-2">
                {chips.map((value) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => onDraftChange(String(value))}
                        className={cn(
                            "border border-line-base bg-surface-raised px-3 py-1.5 transition-colors hover:border-line-dark",
                            amount === value &&
                                "border-foreground-base bg-surface-inverse-raised text-foreground-inverse"
                        )}
                    >
                        <Text variant="fine-print" render={<span />}>
                            {formatMagnitude(value)}
                        </Text>
                    </button>
                ))}
            </div>
        </div>
    );
}

export function RangeAxisInput({
    draft,
    onDraftChange,
    floor,
    ceiling,
    unit
}: {
    draft: string;
    onDraftChange: (value: string) => void;
    floor: number;
    ceiling: number;
    unit: string;
}): React.ReactElement {
    const value = Math.max(floor, Math.min(ceiling, Number(draft) || floor));

    return (
        <div className="space-y-4">
            <div className="relative pt-8">
                <Text
                    variant="number"
                    render={<span />}
                    className="absolute -top-0.5 -translate-x-1/2 text-foreground-base text-lg"
                    style={{
                        left: `${((value - floor) / (ceiling - floor)) * 100}%`
                    }}
                >
                    {value.toLocaleString()}
                </Text>
                <EditorialSlider
                    value={value}
                    onValueChange={(next) => onDraftChange(String(next))}
                    min={floor}
                    max={ceiling}
                    step={Math.max(1, Math.round((ceiling - floor) / 100))}
                />
                <div className="mt-1 flex justify-between">
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        {floor.toLocaleString()}
                    </Text>
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        {ceiling.toLocaleString()}
                    </Text>
                </div>
            </div>
            <div className="flex justify-end">
                <InputGroup className="h-10 w-48">
                    <InputGroupAddon>
                        <InputGroupText>{unit}</InputGroupText>
                    </InputGroupAddon>
                    <InputGroupInput
                        value={draft}
                        inputMode="numeric"
                        onChange={(event) => {
                            const filtered = event.target.value.replace(
                                /\D/g,
                                ""
                            );
                            const n = Number(filtered);
                            onDraftChange(
                                String(Math.max(floor, Math.min(ceiling, n)))
                            );
                        }}
                        numeric
                        className="h-full text-right"
                    />
                </InputGroup>
            </div>
        </div>
    );
}

export function OptionsEditor({
    constraint,
    draft,
    onDraftChange
}: {
    constraint: OptionPolicyConstraint;
    draft: string;
    onDraftChange: (value: string) => void;
}): React.ReactElement {
    return (
        <RadioGroup
            value={draft}
            onValueChange={(value) => onDraftChange(String(value))}
            className="gap-2"
        >
            {constraint.options.map((option) => {
                const selected = draft === option.value;
                return (
                    <label
                        key={option.value}
                        className={cn(
                            "flex w-full cursor-pointer items-center gap-3 border px-4 py-3 transition-colors",
                            selected
                                ? "border-foreground-base bg-surface-overlay"
                                : "border-line-base bg-surface-raised hover:border-line-dark"
                        )}
                    >
                        <RadioGroupItem value={option.value} />
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base"
                        >
                            {option.label}
                        </Text>
                    </label>
                );
            })}
        </RadioGroup>
    );
}
